import validator from 'validator'
import {
  validateIdentifier,
  validatePhone,
} from '@containers/common/validators'
import { SCHOOL_FIELD_INPUT_LIMITS } from '@lib/constants/competitions'
import { t } from '@lingui/macro'

// TODO: add i18n support to validation errors
export const validateTrialBookingFormData = (data) => {
  const errors = {}

  if (!validator.isEmail(data.email)) {
    errors['email'] = [t`Please enter a valid email`]
  }

  if (validator.isEmpty(data.name)) {
    errors['name'] = [t`Please enter your name`]
  }

  // One field on screen, so both modes report under `phone`.
  const identifierErrors = validateIdentifier(data.phone, data.identifierMode)
  if (Object.keys(identifierErrors).length) {
    errors['phone'] = identifierErrors['phone']
  }

  if (!data.termsAndConditions) {
    errors['termsAndConditions'] = [
      t`Please accept terms and conditions to proceed`,
    ]
  }

  if (data.childGrade.value === 'select') {
    errors['childGrade'] = [t`Please select student grade`]
  }

  if (data.childGender.value === 'select') {
    errors['childGender'] = [t`Please select student gender`]
  }

  return errors
}

export const validateCompetitionRegistrationFormData = (data) => {
  const errors = {}

  if (validator.isEmpty(data.name)) {
    errors['name'] = [t`Please enter your name`]
  }

  if (!validator.isEmpty(data.email) && !validator.isEmail(data.email)) {
    errors['email'] = [t`Please enter a valid email`]
  }

  const phoneErrors = validateIdentifier(data.phone, 'phone')
  if (Object.keys(phoneErrors).length) {
    errors['phone'] = phoneErrors['phone']
  }

  if (!data.termsAndConditions) {
    errors['termsAndConditions'] = [
      t`Please accept terms and conditions to proceed`,
    ]
  }

  if (data.childGrade.value === 'select') {
    errors['childGrade'] = [t`Please select student grade`]
  }

  return errors
}

export const validateStudentDetailsData = (
  data,
  collectIdentifiersOnly?: boolean
) => {
  const errors = {}
  // Guarded rather than assumed: every caller sends `email` today, but the
  // field is conditional on other surfaces and a hidden field that blocks
  // submit is a failure the user cannot see or fix.
  if ('email' in data && !validator.isEmail(data.email)) {
    errors['email'] = [t`Please enter a valid email`]
  }

  // Only present for users still on a placeholder number, where this modal is
  // the step that collects the real one.
  if ('phone' in data) {
    const phoneErrors = validatePhone((data.phone || '').replace(/^\+/, ''))
    if (Object.keys(phoneErrors).length) {
      errors['phone'] = phoneErrors['phone']
    }
  }

  if (validator.isEmpty(data.name)) {
    errors['name'] = [t`Please enter your name`]
  }
  // Gender is not on the form when we are only collecting identifiers, so
  // validating it would block a submit on a field nobody can see.
  if (!collectIdentifiersOnly) {
    if (data.gender === 'select') {
      errors['gender'] = [t`Please select student gender`]
    }
  }
  return errors
}

export const validateSchoolData = (schoolName: string) => {
  const errors = {}
  const { MIN_LENGTH, MAX_LENGTH } = SCHOOL_FIELD_INPUT_LIMITS

  if (validator.isEmpty(schoolName))
    errors['school'] = [t`Please add a school name`]

  if (schoolName?.trim().length < MIN_LENGTH)
    errors['school'] = [t`Please select a school with minimum 3 characters`]

  if (schoolName?.trim().length > MAX_LENGTH)
    errors['school'] = [t`Please select a school with maximum 255 characters`]

  return errors
}

type ProfileDataType = {
  name: string
  student: { grade: string }
  bio?: string
  hobbies?: string
}

export const validateProfileData = (data: ProfileDataType) => {
  const errors = {}

  if (data.name && validator.isEmpty(data.name)) {
    errors['name'] = [t`Please enter your name`]
  }

  if (data.student?.grade === 'select') {
    errors['childGrade'] = [t`Please select student grade`]
  }

  return errors
}
